import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ReactNode, useRef } from 'react';

interface DockItemData {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  className?: string;
}

interface DockProps {
  items: DockItemData[];
  className?: string;
  distance?: number;
  panelHeight?: number;
  baseItemSize?: number;
  magnification?: number;
}

function DockItem({ 
  item, 
  mouseX, 
  distance, 
  baseItemSize, 
  magnification 
}: { 
  item: DockItemData; 
  mouseX: any; 
  distance: number; 
  baseItemSize: number; 
  magnification: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  
  const distanceFromMouse = useTransform(mouseX, (val: number) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    const center = bounds.x + bounds.width / 2;
    return Math.abs(val - center);
  });

  const widthSync = useTransform(
    distanceFromMouse,
    [0, distance],
    [magnification, baseItemSize]
  );

  const width = useSpring(widthSync, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  return (
    <motion.div
      ref={ref}
      style={{ width, height: width }}
      onClick={item.onClick}
      className={`
        flex items-center justify-center rounded-xl cursor-pointer
        bg-white/90 backdrop-blur-sm border border-gray-200/50
        shadow-lg hover:shadow-xl transition-shadow duration-200
        ${item.className || ''}
      `}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title={item.label}
    >
      <div className="[&>svg]:!text-gray-600 [&>div]:hover:!text-white transition-colors">
        {item.icon}
      </div>
    </motion.div>
  );
}

export function Dock({
  items,
  className = '',
  distance = 200,
  panelHeight = 68,
  baseItemSize = 40,
  magnification = 60,
}: DockProps) {
  const mouseX = useMotionValue(Infinity);

  return (
    <motion.div
      onMouseMove={(e) => mouseX.set(e.pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      style={{ height: panelHeight }}
      className={`
        inline-flex items-center justify-center gap-1 px-2
        bg-white/80 backdrop-blur-md rounded-2xl
        border border-gray-200/50 shadow-2xl
        ${className}
      `}
    >
      {items.map((item, idx) => (
        <DockItem
          key={idx}
          item={item}
          mouseX={mouseX}
          distance={distance}
          baseItemSize={baseItemSize}
          magnification={magnification}
        />
      ))}
    </motion.div>
  );
}
